import { Badge } from '@safetyhub/ui';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { SeverityBadge } from './SeverityBadge';
import { useModalStore } from '../../../stores/modal';
import type { AIAnalysisDetail } from '../../../lib/inspections';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';
type ResolutionStatus = 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';

interface PreviousFinding {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  photoUrl?: string;
  thumbnailUrl?: string;
  foundAt: string; // ISO date string
  resolutionStatus: ResolutionStatus;
  resolutionPhotoUrl?: string;
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  notes?: string;
  resolvedAt?: string;
  lastActionInspectionId?: string;
  /** 사진 업로더 ID (권한 체크용) */
  photoAddedById?: string;
  /** 원래 점검 ID (어노테이션 조회용) */
  sourceInspectionId?: string;
  /** 원래 사진 ID (어노테이션 조회용) */
  photoId?: string;
  /** AI 분석 상세 (상세 모달용) */
  aiAnalysisDetail?: AIAnalysisDetail;
  /** 보고서 최종 개선대책 */
  reportMeasure?: string;
  /** 보고서 최종 위험요인 */
  reportRisk?: string;
}

interface PreviousFindingCardProps {
  finding: PreviousFinding;
  /** 현재 점검 ID (조치결과 입력 여부 확인용) */
  currentInspectionId: string;
  /** 현재 사용자 ID (권한 체크용) */
  currentUserId?: string;
  /** 조직 Owner 여부 (권한 체크용) */
  isOwner?: boolean;
  /** 현재 점검 주 담당자 ID (권한 체크용) */
  inspectorId?: string;
  /** 보고서 생성 여부 (생성된 경우 조치결과 수정 불가) */
  hasReport?: boolean;
  /** 다음 점검에 보고서가 존재하는지 여부 (다음 점검 보고서 존재 시 조치결과 입력 차단) */
  nextInspectionHasReport?: boolean;
  /** 예약 상태 여부 (점검 시작 전 읽기전용) */
  isScheduled?: boolean;
  /** 사진 클릭 (확대) */
  onPhotoClick?: (url: string) => void;
  /** 복수 사진 클릭 (확대) - 전체 사진 배열 + 시작 인덱스 */
  onResolutionPhotosClick?: (photos: Array<{ id: string; url: string; thumbnailUrl?: string }>, startIndex: number) => void;
  /** 텍스트 영역 클릭 (상세 모달) */
  onDetailClick?: () => void;
  /** ... 메뉴 클릭 */
  onMenuClick?: () => void;
  /** 레이아웃 */
  layout?: 'compact' | 'wide';
  /** 다음 점검일 (ISO 8601, 날짜 선택 max 제한용) */
  nextInspectionDate?: string | null;
  /** 현재 점검 시작일 (ISO 8601, 날짜 선택 min 제한용) */
  inspectionCreatedAt?: string;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: RiskAssessmentConfig | null;
}

/**
 * 이전 미해결 위험요인 카드
 *
 * - 조치 결과 / 조치 결과 수정 버튼만 표시
 * - 버튼 클릭 시 ActionResultModal 열림
 * - 텍스트 영역 클릭 시 AI 분석 상세 모달 열림
 * - 권한: 조직 Owner, 주 담당자, 사진 업로더만 조치결과 입력 가능
 */
export function PreviousFindingCard({
  finding,
  currentInspectionId,
  currentUserId,
  isOwner = false,
  inspectorId,
  hasReport = false,
  nextInspectionHasReport = false,
  isScheduled = false,
  onPhotoClick,
  onResolutionPhotosClick,
  onDetailClick,
  onMenuClick,
  nextInspectionDate,
  inspectionCreatedAt,
  riskConfig,
}: PreviousFindingCardProps) {
  const { openActionResult } = useModalStore();

  const isResolved = finding.resolutionStatus === 'COMPLIED';

  // 이번 점검에서 조치결과가 입력되었는지 확인
  const hasActionResult = finding.lastActionInspectionId === currentInspectionId;

  // 권한 체크: 조직 Owner, 주 담당자, 사진 업로더만 조치결과 입력 가능
  const isInspector = currentUserId && inspectorId && currentUserId === inspectorId;
  const isPhotoUploader = currentUserId && finding.photoAddedById && currentUserId === finding.photoAddedById;
  const canSubmitActionResult = isOwner || isInspector || isPhotoUploader;

  // AI 분석 상세가 있으면 텍스트 클릭 가능
  const hasDetail = !!finding.aiAnalysisDetail;

  // 발견일 포맷
  const foundDate = new Date(finding.foundAt).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleActionResultClick = () => {
    if (!canSubmitActionResult) return;

    // 이번 점검에서 조치결과를 입력한 경우에만 기존 값 전달
    // 이전 점검에서 입력한 데이터는 새 점검에서 빈 상태로 시작
    const isCurrentInspectionAction = hasActionResult;

    openActionResult({
      inspectionId: currentInspectionId,
      findingId: finding.id,
      severity: finding.severity,
      existingNotes: isCurrentInspectionAction ? finding.notes : undefined,
      existingResolutionPhotoUrl: isCurrentInspectionAction ? finding.resolutionPhotoUrl : undefined,
      existingResolutionPhotos: isCurrentInspectionAction ? finding.resolutionPhotos : undefined,
      existingIsResolved: isCurrentInspectionAction ? isResolved : false,
      existingResolvedAt: isCurrentInspectionAction && isResolved ? finding.resolvedAt : undefined,
      measures: finding.aiAnalysisDetail?.recommendation?.measures,
      nextInspectionDate: nextInspectionDate ?? undefined,
      inspectionCreatedAt,
    });
  };

  const handleContentClick = () => {
    if (hasDetail && onDetailClick) {
      onDetailClick();
    }
  };

  const hasBothPhotos = !!(finding.photoUrl && finding.resolutionPhotoUrl);

  // 복수 resolution 사진 목록 (resolutionPhotos가 있으면 사용, 없으면 resolutionPhotoUrl로 fallback)
  const resolutionPhotoList: Array<{ id: string; url: string; thumbnailUrl?: string }> =
    finding.resolutionPhotos && finding.resolutionPhotos.length > 0
      ? finding.resolutionPhotos.map((p, i) => ({ id: p.fileId || `res-${i}`, url: p.url, thumbnailUrl: p.thumbnailUrl }))
      : finding.resolutionPhotoUrl
        ? [{ id: `res-main`, url: finding.resolutionPhotoUrl }]
        : [];

  const hasMultipleResolutionPhotos = resolutionPhotoList.length > 1;

  const handleResolutionPhotoClick = (index: number) => {
    if (hasMultipleResolutionPhotos && onResolutionPhotosClick) {
      onResolutionPhotosClick(resolutionPhotoList, index);
    } else if (resolutionPhotoList[index]) {
      onPhotoClick?.(resolutionPhotoList[index].url);
    }
  };

  return (
    <div
      className={`
        bg-white border rounded-card overflow-hidden flex flex-col
        ${isResolved ? 'border-green-200 bg-green-50/30' : hasActionResult ? 'border-brand-200' : 'border-orange-200'}
      `}
    >
      {/* 사진 영역 - 항상 상단 */}
      {finding.photoUrl && (
        <div className="flex-shrink-0">
          <div className={`${hasBothPhotos ? 'flex items-stretch' : ''}`}>
            {/* 문제 사진 */}
            <div
              className={`
                relative bg-ink-100 cursor-pointer
                ${hasBothPhotos ? 'flex-1 min-w-0' : 'w-full'}
                h-40
              `}
              onClick={() => onPhotoClick?.(finding.photoUrl!)}
            >
              <img
                src={finding.thumbnailUrl || finding.photoUrl}
                alt="이전 점검 사진"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 left-2">
                <span className="bg-ink-800/70 text-white px-1.5 py-0.5 rounded text-xs">
                  이전
                </span>
              </div>
            </div>

            {/* 화살표 (해결됨만) + 대표 사진 */}
            {hasBothPhotos && (
              <>
                {isResolved && (
                  <div className="flex-shrink-0 flex items-center px-1.5 bg-surface-soft">
                    <span className="text-ink-400 text-lg">&rarr;</span>
                  </div>
                )}
                <div
                  className="relative flex-1 min-w-0 h-40 bg-ink-100 cursor-pointer"
                  onClick={() => handleResolutionPhotoClick(0)}
                >
                  <img
                    src={resolutionPhotoList[0]?.thumbnailUrl || finding.resolutionPhotoUrl!}
                    alt={isResolved ? '해결 증빙 사진' : '현재 상황 사진'}
                    className="w-full h-full object-cover"
                  />
                  {/* 복수 사진 배지 */}
                  {hasMultipleResolutionPhotos && (
                    <div className="absolute bottom-2 right-2">
                      <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-xs font-medium">
                        +{resolutionPhotoList.length - 1}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

        </div>
      )}

      {/* 내용 영역 - 항상 사진 아래 */}
      <div className="flex-1 p-3 flex flex-col min-w-0">
        {/* 클릭 가능한 텍스트 영역 */}
        <div
          className={hasDetail ? 'cursor-pointer hover:bg-surface-hover -mx-3 -mt-3 px-3 pt-3 rounded-t-lg transition-colors' : ''}
          onClick={handleContentClick}
        >
          {/* 헤더 */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge tone={isResolved ? 'emerald' : 'amber'} shape="rounded">
                {isResolved ? '해결됨' : '미해결'}
              </Badge>
              <SeverityBadge likelihoodScore={finding.aiLikelihoodScore} severityScore={finding.aiSeverityScore} config={riskConfig} />
              <span className="text-sm text-ink-600 truncate">{finding.category}</span>
            </div>
            {onMenuClick && (
              <button
                onClick={(e) => { e.stopPropagation(); onMenuClick(); }}
                className="text-ink-400 hover:text-ink-600 p-1 -mr-1"
              >
                <span className="text-lg">...</span>
              </button>
            )}
          </div>

          {/* 설명 — 정제된 위험요인 우선 */}
          <p className="text-sm text-ink-700 line-clamp-2 mb-2">
            {finding.reportRisk || finding.description}
          </p>

          {/* 발견일 및 상태 */}
          <p className="text-xs text-ink-400 mb-3">
            {foundDate} 발견
            {isResolved && ' | 해결됨'}
            {!isResolved && hasActionResult && ' | 미해결 확인됨'}
            {hasDetail && (
              <span className="ml-1 text-brand-500">| 상세보기</span>
            )}
          </p>
        </div>

        {/* 개선대책 (정제 데이터) */}
        {finding.reportMeasure && (
          <div className="mb-3 p-2 bg-brand-50 rounded-card-sm text-sm text-ink-700">
            <span className="text-xs text-brand-500 block mb-1">개선대책</span>
            <p className="line-clamp-3">{finding.reportMeasure}</p>
          </div>
        )}

        {/* 조치 사항 표시 */}
        {finding.notes && (
          <div
            className={`mb-3 p-2 bg-surface-soft rounded-card-sm text-sm text-ink-600 ${hasDetail ? 'cursor-pointer hover:bg-surface-hover transition-colors' : ''}`}
            onClick={handleContentClick}
          >
            <span className="text-xs text-ink-400 block mb-1">조치 사항</span>
            <p className="line-clamp-2">{finding.notes}</p>
          </div>
        )}

        {/* 조치 결과 버튼 - mt-auto로 하단 고정 */}
        <div className="mt-auto pt-2">
          {!hasReport && !nextInspectionHasReport && canSubmitActionResult && (!isResolved || hasActionResult) && (
            <button
              type="button"
              onClick={handleActionResultClick}
              className={`
                w-full py-2 px-4 rounded-control text-sm font-medium transition-colors
                ${
                  isResolved
                    ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                    : hasActionResult
                      ? 'bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200'
                      : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                }
              `}
            >
              {hasActionResult ? '조치 결과 수정' : '조치 결과 입력'}
            </button>
          )}

          {isScheduled && (
            <p className="text-xs text-ink-400 italic">
              예약 상태이므로 점검 시작 후 수정할 수 있습니다
            </p>
          )}
          {!isScheduled && hasReport && (
            <p className="text-xs text-ink-400 italic">
              보고서가 생성되어 수정할 수 없습니다
            </p>
          )}
          {!isScheduled && !hasReport && nextInspectionHasReport && (
            <p className="text-xs text-ink-400 italic">
              다음 점검에 보고서가 존재합니다. 해당 점검에서 조치결과를 처리해주세요.
            </p>
          )}

          {!hasReport && !nextInspectionHasReport && !canSubmitActionResult && !hasActionResult && (
            <p className="text-xs text-ink-400 italic">
              조치결과 입력 권한이 없습니다
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 이전 미해결 섹션 헤더
 */
export function PreviousFindingsHeader({
  count,
  unreviewedCount,
  isExpanded,
  onToggle,
}: {
  count: number;
  unreviewedCount?: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-2 text-left"
    >
      <div className="flex items-center gap-2">
        <span className="font-medium text-ink-800">이전 미해결</span>
        <span className="text-sm text-orange-600 font-medium">({count}건)</span>
        {unreviewedCount !== undefined && unreviewedCount > 0 && (
          <span className="text-xs text-red-500 font-medium">
            미입력 {unreviewedCount}건
          </span>
        )}
      </div>
      <span className={`text-ink-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
        <ChevronDownIcon />
      </span>
    </button>
  );
}

function ChevronDownIcon() {
  return <span className="text-lg leading-none inline-block">&#9662;</span>;
}

export default PreviousFindingCard;
